import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Trash2 } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { useAuth } from "@/contexts/AuthContext";
import { useContractTypes } from "@/hooks/useSupabase";
import { PdfDropzone } from "@/components/common/PdfDropzone";
import { InstructionWordLimit, instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  insertContractTypeGuidelineDocument,
  listContractTypeGuidelineDocuments,
} from "@/lib/guideline-attachments";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import { getKnowledgeDocSignedUrl, uploadKnowledgeDoc } from "@/lib/knowledge-docs";
import type { ContractTypeGuidelineDocumentRecord } from "@/types";

export interface ContractTypeSheetDraft {
  mode: "create" | "edit" | "customize";
  id?: string;
  name: string;
  value: string;
  guidelines: string;
}

interface ContractTypeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: ContractTypeSheetDraft | null;
  onSaved?: (value: string) => void;
}

interface PendingDoc {
  key: string;
  file: File;
  extractedText: string;
}

export function ContractTypeSheet({ open, onOpenChange, draft, onSaved }: ContractTypeSheetProps) {
  const { user, getAccessToken } = useAuth();
  const { upsertUserType } = useContractTypes();

  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [guidelines, setGuidelines] = useState("");
  const [savedDocs, setSavedDocs] = useState<ContractTypeGuidelineDocumentRecord[]>([]);
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set());
  const [pendingDocs, setPendingDocs] = useState<PendingDoc[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const title = useMemo(() => {
    if (!draft) return "Contract type";
    if (draft.mode === "create") return "New contract type";
    if (draft.mode === "edit") return "Edit contract type";
    return "Customize contract type";
  }, [draft]);

  useEffect(() => {
    if (!open || !draft) return;
    setName(draft.name);
    setValue(draft.value);
    setGuidelines(draft.guidelines);
    setRemovedIds(new Set());
    setPendingDocs([]);
    setLoadError(null);
    setSaveError(null);
    setCreatedId(null);

    if (draft.mode === "edit" && draft.id) {
      void listContractTypeGuidelineDocuments(draft.id)
        .then(rows => {
          setSavedDocs(rows);
        })
        .catch((e: unknown) => {
          console.error(e);
          setLoadError(e instanceof Error ? e.message : "Failed to load documents");
          setSavedDocs([]);
        });
    } else {
      setSavedDocs([]);
    }
  }, [open, draft]);

  useEffect(() => {
    let cancelled = false;
    async function resolveUrls() {
      const visible = savedDocs.filter(d => !removedIds.has(d.id));
      const next: Record<string, string> = {};
      await Promise.all(
        visible.map(async d => {
          const url = await getKnowledgeDocSignedUrl(d.file_path);
          if (url) next[d.id] = url;
        })
      );
      if (!cancelled) setSignedUrls(next);
    }
    void resolveUrls();
    return () => {
      cancelled = true;
    };
  }, [savedDocs, removedIds]);

  const visibleSaved = savedDocs.filter(d => !removedIds.has(d.id));
  const overLimit = instructionsExceedLimit(guidelines);
  const hasReferenceMaterial =
    guidelines.trim().length > 0 || visibleSaved.length > 0 || pendingDocs.length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!user || !draft) return;
    if (!hasReferenceMaterial || overLimit) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const existingId = draft.mode === "edit" ? draft.id : createdId ?? undefined;
      const omitIds = [...removedIds];
      if (existingId) {
        for (const p of pendingDocs) {
          const uploaded = await uploadKnowledgeDoc(p.file, "contract-types");
          await insertContractTypeGuidelineDocument({
            contract_type_id: existingId,
            file_path: uploaded.path,
            file_name: uploaded.fileName,
            extracted_text: p.extractedText,
          });
          setPendingDocs((prev) => prev.filter((doc) => doc.key !== p.key));
        }
      }

      const typeId = await upsertUserType({
        id: existingId,
        name: name.trim(),
        value: value.trim(),
        guidelines: guidelines.trim(),
        omit_document_ids: omitIds,
      });

      if (!existingId) {
        setCreatedId(typeId);
        for (const p of pendingDocs) {
          const uploaded = await uploadKnowledgeDoc(p.file, "contract-types");
          await insertContractTypeGuidelineDocument({
            contract_type_id: typeId,
            file_path: uploaded.path,
            file_name: uploaded.fileName,
            extracted_text: p.extractedText,
          });
          setPendingDocs((prev) => prev.filter((doc) => doc.key !== p.key));
        }
      }

      setPendingDocs([]);
      setRemovedIds(new Set());
      onSaved?.(value.trim());
      onOpenChange(false);
    } catch (reason) {
      console.error(reason);
      setSaveError(reason instanceof Error ? reason.message : "Failed to save contract type");
    } finally {
      setIsSaving(false);
    }
  }

  const queuePdf = useCallback((payload: { file: File; extractedText: string }) => {
    setPendingDocs(prev => [...prev, { key: crypto.randomUUID(), ...payload }]);
  }, []);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="p-0">
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className={pageDescriptionMobileHiddenClassName}>
              Guidelines are injected into every review and have a 1,500-word limit. Reference PDFs have no word limit: they are searched during the review and are not pasted into the prompt.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ct-name">
                Name
              </Label>
              <Input
                id="ct-name"
                placeholder="Master Service Agreement"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ct-short">
                Short name
              </Label>
              <Input
                id="ct-short"
                placeholder="e.g. NDA, MSA"
                value={value}
                onChange={e => setValue(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ct-guidelines">
                Contract type guidelines
              </Label>
              <Textarea
                id="ct-guidelines"
                rows={8}
                placeholder="Guidelines injected into the review prompt (optional if you add reference PDFs below)"
                value={guidelines}
                onChange={e => setGuidelines(e.target.value)}
              />
              <InstructionWordLimit
                text={guidelines}
                kind="review-guidelines"
                disabled={isSaving}
                onSummarized={setGuidelines}
              />
            </div>

            <PdfDropzone
              mode="queue"
              getAccessToken={getAccessToken}
              onQueued={queuePdf}
              fieldLabel="Reference PDFs (optional)"
              disabled={isSaving}
            />

            {loadError ? <p className="text-xs text-[var(--tint-red-fg)] dark:text-red-400">{loadError}</p> : null}
            {saveError ? <p className="text-xs text-[var(--tint-red-fg)] dark:text-red-400">{saveError}</p> : null}

            {visibleSaved.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">Saved reference PDFs</p>
                <ul className="space-y-2">
                  {visibleSaved.map(doc => (
                    <li
                      key={doc.id}
                      className="flex items-center gap-2 rounded-md border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-3 py-2 text-xs dark:border-white/10 dark:bg-slate-900/50"
                    >
                      <span className="min-w-0 flex-1 truncate text-[var(--lm-foreground)] dark:text-slate-300" title={doc.file_name}>
                        {doc.file_name}
                      </span>
                      {signedUrls[doc.id] ? (
                        <a
                          href={signedUrls[doc.id]}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 text-[var(--lm-accent)] hover:text-[#6d28d9] dark:text-violet-300 dark:hover:text-violet-200"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open
                        </a>
                      ) : null}
                      <IconButton
                        type="button"
                        size="sm"
                        variant="destructive"
                        title="Remove"
                        onClick={() => setRemovedIds(prev => new Set(prev).add(doc.id))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {pendingDocs.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">Pending upload (saved with this type)</p>
                <ul className="space-y-2">
                  {pendingDocs.map(p => (
                    <li
                      key={p.key}
                      className="flex items-center gap-2 rounded-md border border-[var(--lm-border-card)] bg-[var(--surface-sunken)] px-3 py-2 text-xs dark:border-violet-400/25 dark:bg-slate-900/50"
                    >
                      <span className="min-w-0 flex-1 truncate text-[var(--lm-foreground)] dark:text-slate-300" title={p.file.name}>
                        {p.file.name}
                      </span>
                      <IconButton
                        type="button"
                        size="sm"
                        variant="destructive"
                        title="Remove"
                        onClick={() => setPendingDocs(prev => prev.filter(x => x.key !== p.key))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!hasReferenceMaterial ? (
              <p className="text-xs text-[var(--tint-amber-fg)] dark:text-amber-200/90">Add guidelines text, at least one reference PDF, or both.</p>
            ) : null}
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="surface" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !hasReferenceMaterial || overLimit}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
